import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, DollarSign, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getDateInputValue } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Commissions = () => {
  const [commissions, setCommissions] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    policy_id: '', commission_type: 'first-year', amount: 0, date: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [commissionsRes, policiesRes, summaryRes] = await Promise.all([
        axios.get(`${API}/commissions`, { withCredentials: true }),
        axios.get(`${API}/policies`, { withCredentials: true }),
        axios.get(`${API}/commissions/summary`, { withCredentials: true })
      ]);
      setCommissions(commissionsRes.data);
      setPolicies(policiesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/commissions`, formData, { withCredentials: true });
      toast.success('Commission logged successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save commission');
    }
  };

  const resetForm = () => {
    setFormData({
      policy_id: '', commission_type: 'first-year', amount: 0, date: ''
    });
  };

  const getPolicyNumber = (policyId) => {
    const policy = policies.find(p => p.policy_id === policyId);
    return policy ? policy.policy_number : 'Unknown';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="commissions-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Commission Tracker</h1>
          <p className="text-slate-600 mt-1">Track your earnings and commission history</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-commission-button">
              <Plus className="h-4 w-4 mr-2" /> Log Commission
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Commission</DialogTitle>
              <DialogDescription>Record a commission payment</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="policy_id">Policy *</Label>
                <Select value={formData.policy_id} onValueChange={(value) => setFormData({...formData, policy_id: value})} required>
                  <SelectTrigger data-testid="commission-policy-select"><SelectValue placeholder="Select policy" /></SelectTrigger>
                  <SelectContent>
                    {policies.map(policy => (
                      <SelectItem key={policy.policy_id} value={policy.policy_id}>{policy.policy_number} - {policy.product_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="commission_type">Type *</Label>
                <Select value={formData.commission_type} onValueChange={(value) => setFormData({...formData, commission_type: value})}>
                  <SelectTrigger data-testid="commission-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first-year">First Year</SelectItem>
                    <SelectItem value="renewal">Renewal</SelectItem>
                    <SelectItem value="bonus">Bonus</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Amount (₹) *</Label>
                <Input id="amount" type="number" value={formData.amount} onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value) || 0})} required data-testid="commission-amount-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input id="date" type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required data-testid="commission-date-input" />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="commission-submit-button">Log Commission</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Monthly Total</p>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope' }}>{formatCurrency(summary?.monthly_total || 0)}</p>
              </div>
              <DollarSign className="h-12 w-12 text-green-600 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Yearly Total</p>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope' }}>{formatCurrency(summary?.yearly_total || 0)}</p>
              </div>
              <TrendingUp className="h-12 w-12 text-blue-600 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Total Entries</p>
                <p className="text-3xl font-bold mt-2" style={{ fontFamily: 'Manrope' }}>{summary?.total_commissions || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-2xl font-bold text-purple-600">#</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Commission History ({commissions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {commissions.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No commissions logged yet. Start tracking your earnings!</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Policy Number</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {commissions.map((commission) => (
                  <TableRow key={commission.commission_id} data-testid={`commission-row-${commission.commission_id}`}>
                    <TableCell>{formatDate(commission.date)}</TableCell>
                    <TableCell className="font-medium">{getPolicyNumber(commission.policy_id)}</TableCell>
                    <TableCell className="capitalize">{commission.commission_type}</TableCell>
                    <TableCell className="text-right font-semibold text-green-600">{formatCurrency(commission.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Commissions;